import RpcService from '@/core/services/Rpc/RpcService';
import RpcResponse from '@/core/services/Rpc/RpcResponse';

import type Assembly          from '@/domains/Assemblies/Assembly';
import type Company           from '@/domains/Companies/Company';
import type DeliveryType      from '@/domains/DeliveryTypes/DeliveryType';
import type Person            from '@/domains/Persons/Person';
import type ProductCategory   from '@/domains/ProductCategories/ProductCategory';
import type RpcDataPagination from '@/core/services/Rpc/RpcDataPagination';
import type RpcDataSort       from '@/core/services/Rpc/RpcDataSort';
import type Partner           from '@/domains/Partners/Partner';
import type DateRange         from "@/core/types/DateRange";

/**
 * DAO для работы со сборками
 */
class AssembliesDAO {

	/**
	 * Загружает одну сборку по ID
	 */
	static async get(id: number, isAdminMode?: boolean): Promise<RpcResponse> {
		return await RpcService.makeRequest('assemblies.get', { id, isAdminMode });
	}

	/**
	 * Загружает список сборок
	 */
	static async getList(
		filters?: {
			id?:                      number;
			idPattern?:               number;
			isOfferBased?:            boolean;
			isSamples?:               boolean;
			orderCategory?:           object;
			deliveryType?:            DeliveryType;
			seller?:                  Partner;
			sellerCompany?:           Company;
			buyer?:                   Partner;
			buyerCompany?:            Company;
			responsible?:             Person;
			productCategory?:         ProductCategory;
			namePattern?:             string;
			codePattern?:             string;
			isShipped?:               boolean;
			isShippedEntirely?:       boolean;
			isTransportOrderCreated?: boolean;
			desiredReceiptPeriod?:    DateRange;
			plannedShipmentPeriod?:   DateRange;
			assemblyPeriod?:          DateRange;
			isAdminMode?:             boolean;
			isForTestsOnly?:          boolean;
      requirePositions?:        boolean;
      requireNestedObjects?:    boolean;
      requireCargoUnits?:       boolean;
      requireCommentsInfo?:     boolean;
		},
		pagination?: RpcDataPagination,
		sort?: RpcDataSort,
	): Promise<RpcResponse> {
		return await RpcService.makeRequest(
			'assemblies.getList',
      {
        ...filters,
        orderCategory:           filters?.orderCategory ? { id: (filters.orderCategory as any).id } : undefined,
        deliveryType:            filters?.deliveryType ? { id: filters.deliveryType.id } : undefined,
        seller:                  filters?.seller ? { id: filters.seller.id } : undefined,
        sellerCompany:           filters?.sellerCompany ? { id: filters.sellerCompany.id } : undefined,
        buyer:                   filters?.buyer ? { id: filters.buyer.id } : undefined,
        buyerCompany:            filters?.buyerCompany ? { id: filters.buyerCompany.id } : undefined,
        responsible:             filters?.responsible ? { id: filters.responsible.id } : undefined,
        productCategory:         filters?.productCategory ? { id: filters.productCategory.id } : undefined,
      },
			pagination,
			sort,
			true, null, null, false,
			{ abortPrevious: true },
		);
	}

	/**
	 * Создаёт сборку
	 */
	static async add(assembly: Assembly): Promise<RpcResponse> {
		return await RpcService.makeRequest('assemblies.add', { assembly });
	}

	/**
	 * Обновляет сборку
	 */
	static async update(assembly: Assembly, changeReasonText?: string): Promise<RpcResponse> {
		return await RpcService.makeRequest('assemblies.update', { assembly, changeReasonText });
	}

	/**
	 * Удаляет сборку
	 */
	static async delete(assembly: Assembly, changeReasonText?: string): Promise<RpcResponse> {
		return await RpcService.makeRequest('assemblies.delete', { assembly, changeReasonText });
	}
}

export default AssembliesDAO;